import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useUser } from "@clerk/clerk-react";
import {
  Apple,
  Scale,
  Info,
  Target,
  ArrowRight,
  CheckCircle2,
  Timer,
  HeartPulse,
  AlertCircle,
  Utensils,
  Leaf,
  Beef,
  Fish,
  Wheat,
  Cookie,
  Carrot,
  Crown,
  Lock
} from 'lucide-react';
import { useAnalysis } from '../AnalysisContext';
import { motion } from 'framer-motion';
import NutricionBasica from './nutrition/NutricionBasica';
import TipoAlimentacionCard from './nutrition/TipoAlimentacionCard';
import HidratacionCard from './nutrition/HidratacionCard';
import SenalesDeAlertaCard from './nutrition/SenalesDeAlertaCard';
import PremiosCard from './nutrition/PremiosCard';
import { useNavigate, useLocation } from "react-router-dom";

const PremiumGate = () => (
  <div className="min-h-[80vh] flex items-center justify-center p-3 sm:hidden">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-lg p-4 border border-purple-100"
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="relative mb-1">
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur opacity-25"></div>
          <div className="relative bg-white p-3 rounded-full">
            <Crown className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">Contenido Premium</h2>
          <p className="text-sm text-gray-600">
            Accede a guías nutricionales detalladas para tu mascota
          </p>
        </div>
        
        <div className="w-full grid grid-cols-1 gap-2 my-2">
          {[
            'Planes nutricionales a medida',
            'Guías de alimentación',
            'Recomendaciones de hidratación'
          ].map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-purple-600 flex-shrink-0" />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
        
        <button 
          onClick={() => window.location.href = '/pricing'}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2.5 px-4 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2 text-sm"
        >
          <Lock className="h-4 w-4" />
          <span>Desbloquear Premium</span>
        </button>
      </div>
    </motion.div>
  </div>
);

const NoAnalysisGate = () => (
  <div className="min-h-[80vh] flex items-center justify-center p-3 sm:hidden">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md bg-gradient-to-br from-orange-50 to-white rounded-xl shadow-lg p-4 border border-orange-100"
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="relative mb-1">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full blur opacity-25"></div>
          <div className="relative bg-white p-3 rounded-full">
            <Target className="h-8 w-8 text-orange-500" />
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">Análisis Nutricional</h2>
          <p className="text-sm text-gray-600">
            Realiza un análisis integral para obtener tu plan nutricional personalizado
          </p>
        </div>
        
        <div className="w-full grid grid-cols-1 gap-2 my-2">
          {[
            'Plan nutricional a medida',
            'Guía de alimentación',
            'Control de porciones'
          ].map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-orange-500 flex-shrink-0" />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
        
        <button 
          onClick={() => window.location.href = '/analysis'}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-2.5 px-4 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2 hover:from-orange-600 hover:to-amber-600 text-sm"
        >
          <Target className="h-4 w-4" />
          <span>Realizar Análisis Integral</span>
        </button>
      </div>
    </motion.div>
  </div>
);

const NutritionalTab = () => {
  const { analysisData, breedGuide } = useAnalysis();
  const { user } = useUser();
  const isPremium = user?.unsafeMetadata?.isPremium;

  if (!isPremium) {
    return <PremiumGate />;
  }

  if (!breedGuide?.cuidadosSalud?.nutricion) {
    return <NoAnalysisGate />;
  }

  return (
    <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6">
      {/* Context Notice */}
      <Alert className="bg-orange-50 border-orange-200">
        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500" />
        <AlertDescription className="text-sm sm:text-sm">
          Este plan nutricional está personalizado según las necesidades específicas de tu mascota.
          Consulta con tu veterinario antes de realizar cambios significativos en la dieta.
        </AlertDescription>
      </Alert>

      <NutricionBasica descripcion={breedGuide?.cuidadosSalud?.nutricion?.descripcion} necesidadesDiarias={breedGuide?.cuidadosSalud?.nutricion?.necesidadesDiarias} />

      <TipoAlimentacionCard tipoAlimentacion={breedGuide?.cuidadosSalud?.nutricion?.tipoAlimentacion} />
      <HidratacionCard hidratacion={breedGuide?.cuidadosSalud?.nutricion?.hidratacion} />
      <SenalesDeAlertaCard senalesDeAlerta={breedGuide?.cuidadosSalud?.nutricion?.senalesDeAlerta} />
      <PremiosCard premios={breedGuide?.cuidadosSalud?.nutricion?.premios} />
      
      {/* Important Notes */}
      <Alert className="bg-orange-50 border-orange-200">
        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500" />
        <AlertDescription className="space-y-2 text-sm sm:text-sm text-orange-700">
          <p>
            Este plan nutricional considera diversos factores específicos. Ajusta según:
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>La etapa de vida de tu mascota</li>
            <li>Nivel de actividad física</li>
            <li>Condiciones de salud específicas</li>
            <li>Recomendaciones veterinarias</li>
            <li>Preferencias y tolerancias alimentarias</li>
            <li>Cambios estacionales en la dieta</li>
          </ul>
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-orange-200">
            <div className="flex items-start gap-1.5 sm:gap-2">
              <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500 mt-0.5" />
              <p className="text-sm sm:text-sm">
                Realiza cambios graduales en la dieta y observa la respuesta de tu mascota.
                Mantén un registro de cualquier cambio en el apetito o comportamiento.
              </p>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default NutritionalTab;